import { useStakeholders, useStockClasses } from '@commenda/api';
import {
  StakeholderRelationshipTypeMap,
  fetcher,
  serializeStakeholder,
} from '@commenda/utils';
import { Currency, TransactionType } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import {
  FieldWrapper,
  Modal,
  PrimaryButton,
  SelectInput,
  TextInput,
} from '../../../components';

type AddTransactionModalProps = {
  isAddTransactionModalShown: boolean;
  toggleAddTransactionModalVisibility: () => void;
  token: string;
  mutate: () => void;
  setNotification: (arg0) => void;
  currency: Currency;
};

const AddTransactionModal = ({
  isAddTransactionModalShown,
  toggleAddTransactionModalVisibility,
  token,
  mutate,
  setNotification,
  currency,
}: AddTransactionModalProps) => {
  const router = useRouter();

  const { id, companyId } = router.query;

  const { data: stockClasses } = useStockClasses(+(id as string));

  const { data: stakeholdersData } = useStakeholders(+(id as string), token);

  const [today, setToday] = useState('');

  const stakeholderOptions = stakeholdersData
    ?.map(serializeStakeholder)
    .map((stakeholder) => {
      if (stakeholder?.corporationId !== +id) {
        return {
          displayedValue: `${stakeholder.name} · ${
            StakeholderRelationshipTypeMap[stakeholder.relationshipType]
          }`,
          value: stakeholder.id,
        };
      }
      return null;
    })
    .filter(Boolean);

  const [selectedStakeholingId, setSelectedStakeholdingId] = useState(null);
  const [selectedStockClassId, setSelectedStockClassId] = useState(null);

  const serializedStockClassesOptions = stockClasses?.map((stockClass) => ({
    displayedValue: stockClass.name,
    value: stockClass.id,
  }));

  const [date, setDate] = useState('');
  const [noOfSharesTransferred, setNoOfSharesTransferred] = useState(null);
  const [perSharePrice, setPerSharePrice] = useState(null);

  const onAddTransaction = async () => {
    if (!selectedStockClassId) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please select a share class.',
      });
      return false;
    }

    if (!selectedStakeholingId) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        messsage: 'Please select a stakeholder',
      });
      return false;
    }

    if (!noOfSharesTransferred) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        messsage: 'Please enter a no of shares.',
      });
      return false;
    }

    if (!perSharePrice) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        messsage: 'Please enter price per share.',
      });
      return false;
    }

    const selectedStockClass = stockClasses.find(
      (stockClass) => stockClass.id === selectedStockClassId
    );

    const numberOfSharesLeftForSelectedStockClass =
      selectedStockClass.authorizedShares -
      selectedStockClass.outstandingShares;

    if (noOfSharesTransferred > numberOfSharesLeftForSelectedStockClass) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Share Class does not have enough shares.',
      });
      return false;
    }

    const body = {
      type: TransactionType.TX_STOCK_ISSUANCE,
      stockClassId: selectedStockClassId,
      stakeholdingId: selectedStakeholingId,
      companyId: +(companyId as string),
      additionalInformation: {
        quantity: +noOfSharesTransferred,
        sharePrice: { currency, amount: +perSharePrice },
      },
      ...(date ? { date } : {}),
    };

    const response = await fetcher(
      'capTable/transaction',
      { method: 'POST', body },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Please try again later, or contact team commenda',
      });
      return false;
    }

    await mutate();

    toggleAddTransactionModalVisibility();

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Successfully added the transaction',
    });

    setSelectedStakeholdingId(null);
    setSelectedStockClassId(null);
    setDate(null);
    setNoOfSharesTransferred(null);
    setPerSharePrice(null);

    return true;
  };

  useEffect(() => {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().substring(0, 10);
    setToday(formattedDate);
  }, []);

  return (
    <Modal
      isShown={isAddTransactionModalShown}
      hide={toggleAddTransactionModalVisibility}
      headerText="Add Transaction"
    >
      <div className="flex flex-col gap-3">
        <FieldWrapper label="Choose Stakeholder" id="stakeholder">
          <SelectInput
            options={stakeholderOptions}
            value={selectedStakeholingId}
            setValue={setSelectedStakeholdingId}
          />
        </FieldWrapper>

        <FieldWrapper label="Choose Share Class" id="stockClass">
          <SelectInput
            options={serializedStockClassesOptions}
            value={selectedStockClassId}
            setValue={setSelectedStockClassId}
          />
        </FieldWrapper>

        <FieldWrapper label="Date of Transaction" id="date">
          <TextInput
            type="date"
            id="date"
            placeholder="YYYY-MM-DD"
            value={date}
            setValue={setDate}
            max={today}
          />
        </FieldWrapper>
        <FieldWrapper
          label="Number of Shares Transferred"
          id="noOfSharesTransferred"
        >
          <TextInput
            placeholder="Number of Shares Transferred"
            value={noOfSharesTransferred}
            setValue={setNoOfSharesTransferred}
            type="number"
            id="noOfSharesTransferred"
          />
        </FieldWrapper>
        <FieldWrapper label="Share Transfer Price" id="sharePerPrice">
          <TextInput
            id="sharePerPrice"
            placeholder="Share Transfer Price"
            value={perSharePrice}
            setValue={setPerSharePrice}
            type="number"
          />
        </FieldWrapper>
        <div className="flex gap-4 self-end">
          <PrimaryButton onClick={onAddTransaction}>
            Add Transaction
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default AddTransactionModal;
